"use client";

import React from "react";
import { useRouter } from "next/navigation";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Button } from "../ui/button";
import { QuoteRequestForm } from "../shared/QuoteRequestForm";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../ui/tabs";
import { ImageWithFallback } from "../figma/ImageWithFallback";
import HeroImage from "@/components/ui/hero-image";
import {
  ArrowLeft,
  Phone,
  CheckCircle,
} from "lucide-react";
import { CallToActionSection } from "../shared/CallToActionSection";
import { PhoneLink } from "@/utils/internal/linkHelpers";
import { DISINFESTAZIONE_PAGE } from "@/utils/constants/pages/disinfestazione";
import { CONTACT_INFO } from "@/utils/constants/app";
import { getPestImage, getPestImageClasses } from "@/utils/constants/images";

interface DisinfestationPageProps {
  onBackToHome?: () => void;
  activeTab?: "striscianti" | "volanti" | "aracnidi" | "legno";
  heroImageUrl?: string | null;
}

export function DisinfestationPage({
  onBackToHome,
  activeTab = "striscianti",
  heroImageUrl,
}: DisinfestationPageProps) {
  const router = useRouter();
  const handleBackToHome = () => {
    if (onBackToHome) return onBackToHome();
    router.push("/");
  };
  return (
    <div className="min-h-screen bg-white">
      {/* Header with Back Button */}
      <div className="bg-green-600 text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center mb-6">
            <Button
              variant="ghost"
              onClick={handleBackToHome}
              className="text-white hover:bg-green-700 mr-4"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {DISINFESTAZIONE_PAGE.HEADER.BACK_BUTTON}
            </Button>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div>
                <h1 className="text-4xl lg:text-5xl font-bold mb-2">
                  {DISINFESTAZIONE_PAGE.HEADER.TITLE}
                </h1>
                <p className="text-xl opacity-90">
                  {DISINFESTAZIONE_PAGE.HEADER.SUBTITLE}
                </p>
              </div>
            </div>
            <p className="text-lg opacity-80 max-w-3xl mx-auto">
              {DISINFESTAZIONE_PAGE.HEADER.DESCRIPTION}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Service Overview */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <h2 className="text-3xl lg:text-4xl text-gray-900 mb-8">
                {DISINFESTAZIONE_PAGE.OVERVIEW.TITLE}
              </h2>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                {DISINFESTAZIONE_PAGE.OVERVIEW.DESCRIPTION}
              </p>

              <div className="space-y-6 mb-8">
                {DISINFESTAZIONE_PAGE.OVERVIEW.FEATURES.map(
                  (feature, index) => (
                    <div
                      key={index}
                      className="flex items-start"
                    >
                      <CheckCircle className="w-6 h-6 text-green-600 mr-4 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">
                          {feature.title}
                        </h4>
                        <p className="text-gray-600">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  ),
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 max-w-3xl">
                <QuoteRequestForm
                  showPopupButton={true}
                  popupButtonText={
                    DISINFESTAZIONE_PAGE.OVERVIEW.BUTTONS
                      .PRIMARY
                  }
                  popupButtonClassName="cta-button-enhanced bg-green-600 hover:bg-green-700 text-white px-8 py-4 text-lg font-semibold rounded-lg flex-1 sm:flex-auto sm:min-w-[220px]"
                  title={
                    DISINFESTAZIONE_PAGE.OVERVIEW.FORM.TITLE
                  }
                  subtitle={
                    DISINFESTAZIONE_PAGE.OVERVIEW.FORM.SUBTITLE
                  }
                  defaultService="disinfestazione"
                />
                <PhoneLink
                  phoneNumber={CONTACT_INFO.MAIN_PHONE}
                  className="inline-flex items-center justify-center px-6 py-3 text-base font-medium border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white rounded-lg transition-colors duration-300 flex-shrink-0 sm:min-w-[180px]"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Chiama Ora
                </PhoneLink>
              </div>
            </div>

            <div className="space-y-6">
              <div className="relative w-full h-80 lg:h-96 rounded-2xl shadow-2xl overflow-hidden">
                <HeroImage
                  src={heroImageUrl ?? DISINFESTAZIONE_PAGE.MAIN_IMAGE.URL}
                  alt={DISINFESTAZIONE_PAGE.MAIN_IMAGE.ALT}
                  sizes="(max-width:1024px) 100vw, 50vw"
                  priority
                />
              </div>
            </div>
          </div>

          {/* Pest Types Section */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl text-gray-900 mb-6">
                {DISINFESTAZIONE_PAGE.PEST_TYPES.TITLE}
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {DISINFESTAZIONE_PAGE.PEST_TYPES.SUBTITLE}
              </p>
            </div>

            <Tabs defaultValue={activeTab} className="w-full">
              <TabsList className="!grid w-full grid-cols-2 lg:grid-cols-4 gap-3 mb-8 h-auto rounded-2xl p-3 bg-gray-100 overflow-x-hidden">
                {DISINFESTAZIONE_PAGE.PEST_TYPES.CATEGORIES.map(
                  (category) => {
                    const IconComponent = category.icon;
                    return (
                      <TabsTrigger
                        key={category.tabValue}
                        value={category.tabValue}
                        className="w-full rounded-2xl min-h-[72px] md:min-h-[80px] flex flex-col items-center justify-center p-4 text-center whitespace-normal leading-snug data-[state=active]:bg-green-600 data-[state=active]:text-white"
                      >
                        <IconComponent className="w-6 h-6 mb-2" />
                        <span className="text-sm">
                          {category.category}
                        </span>
                      </TabsTrigger>
                    );
                  },
                )}
              </TabsList>

              {DISINFESTAZIONE_PAGE.PEST_TYPES.CATEGORIES.map(
                (category, categoryIndex) => (
                  <TabsContent
                    key={categoryIndex}
                    value={category.tabValue}
                    className="mt-0"
                  >
                    <div className="space-y-8">
                      {category.pests.map((pest, pestIndex) => (
                        <Card
                          key={pestIndex}
                          className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-shadow duration-300"
                        >
                          <div className="grid lg:grid-cols-3 xl:grid-cols-5 gap-0">
                            {/* Image section - responsive width */}
                            <div className="lg:col-span-1 xl:col-span-2 relative">
                              <div className="relative h-64 sm:h-72 md:h-80 lg:h-80 xl:h-96 2xl:h-[28rem] overflow-hidden flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                                <ImageWithFallback
                                src={getPestImage(pest.name) || pest.image}
                                alt={pest.name}
                                className={`w-full h-full ${getPestImageClasses(pest.name)} transition-transform duration-300 ease-in-out hover:scale-105`}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/20 to-transparent" />
                                <div className="absolute bottom-4 left-4 right-4 text-white z-10">
                                  <h3 className="text-xl lg:text-2xl font-bold mb-1 drop-shadow-lg">
                                    {pest.name}
                                  </h3>
                                  {/* scientific name optional */}
                                </div>
                              </div>
                            </div>

                            {/* Content section - adjusts width based on screen size */}
                            <div className="lg:col-span-2 xl:col-span-3 p-6 sm:p-8 lg:p-8 xl:p-10">
                              <p className="text-base lg:text-lg xl:text-xl text-gray-700 mb-6 lg:mb-8 leading-relaxed">
                                {pest.description}
                              </p>

                              <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
                                <div>
                                  <h4 className="font-semibold text-gray-900 mb-3 lg:mb-4 text-base lg:text-lg">
                                    Caratteristiche
                                  </h4>
                                  <div className="space-y-2 lg:space-y-3">
                                    {pest.characteristics.map(
                                      (char) => (
                                        <div
                                          key={char}
                                          className="flex items-start text-sm lg:text-base"
                                        >
                                          <div className="w-2 h-2 lg:w-2.5 lg:h-2.5 bg-blue-600 rounded-full mr-3 flex-shrink-0 mt-1.5 lg:mt-2" />
                                          <span className="text-gray-700 leading-relaxed">
                                            {char}
                                          </span>
                                        </div>
                                      ),
                                    )}
                                  </div>
                                </div>

                                <div className="space-y-4 lg:space-y-6">
                                  <div>
                                    <h4 className="font-semibold text-gray-900 mb-2 lg:mb-3 text-base lg:text-lg">
                                      Habitat Preferito
                                    </h4>
                                    <p className="text-gray-600 text-sm lg:text-base leading-relaxed">
                                      {pest.habitat}
                                    </p>
                                  </div>

                                  <div>
                                    <h4 className="font-semibold text-gray-900 mb-2 lg:mb-3 text-base lg:text-lg">
                                      Rischi per la Salute
                                    </h4>
                                    <p className="text-gray-600 text-sm lg:text-base leading-relaxed">
                                      {pest.risks}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </TabsContent>
                ),
              )}
            </Tabs>
          </div>

          {/* Techniques Section */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl text-gray-900 mb-6">
                {DISINFESTAZIONE_PAGE.TECHNIQUES.TITLE}
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {DISINFESTAZIONE_PAGE.TECHNIQUES.SUBTITLE}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {DISINFESTAZIONE_PAGE.TECHNIQUES.METHODS.map(
                (technique) => {
                  const IconComponent = technique.icon;
                  return (
                    <Card
                      key={technique.id}
                      className="border-0 shadow-lg hover:shadow-xl transition-shadow"
                    >
                      <CardHeader>
                        <div className="flex items-center mb-4">
                          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                            <IconComponent className="w-6 h-6 text-green-600" />
                          </div>
                          <div>
                            <CardTitle className="text-xl">
                              {technique.title}
                            </CardTitle>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-600 mb-6">
                          {technique.description}
                        </p>

                        <div className="space-y-4">
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-3">
                              Caratteristiche Tecniche
                            </h4>
                            <div className="space-y-2">
                              {technique.features.map(
                                (feature) => (
                                  <div
                                    key={feature}
                                    className="flex items-center text-sm"
                                  >
                                    <CheckCircle className="w-4 h-4 text-green-600 mr-3 flex-shrink-0" />
                                    <span className="text-gray-700">
                                      {feature}
                                    </span>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>

                          <div>
                            <h4 className="font-semibold text-gray-900 mb-3">
                              Applicazioni Principali
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {technique.applications.map(
                                (app) => (
                                  <span
                                    key={app}
                                    className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm"
                                  >
                                    {app}
                                  </span>
                                ),
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                },
              )}
            </div>
          </div>

          {/* Process Timeline */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl text-gray-900 mb-6">
                {DISINFESTAZIONE_PAGE.PROCESS.TITLE}
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {DISINFESTAZIONE_PAGE.PROCESS.SUBTITLE}
              </p>
            </div>

            <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
              {DISINFESTAZIONE_PAGE.PROCESS.PHASES.map(
                (phase) => {
                  const IconComponent = phase.icon;
                  return (
                    <Card
                      key={phase.step + phase.title}
                      className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow relative"
                    >
                      <CardContent className="p-6">
                        <div
                          className={`w-16 h-16 ${phase.color} rounded-full flex items-center justify-center mx-auto mb-4`}
                        >
                          <IconComponent className="w-8 h-8" />
                        </div>
                        <div className="absolute -top-3 -right-3 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          {phase.step}
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">
                          {phase.title}
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          {phase.description}
                        </p>
                      </CardContent>
                    </Card>
                  );
                },
              )}
            </div>
          </div>

          {/* Call to Action Section */}
          <CallToActionSection
            title={DISINFESTAZIONE_PAGE.CTA_SECTION.TITLE}
            description={
              DISINFESTAZIONE_PAGE.CTA_SECTION.DESCRIPTION
            }
            primaryButtonText={
              DISINFESTAZIONE_PAGE.CTA_SECTION.PRIMARY_BUTTON
            }
            formTitle={
              DISINFESTAZIONE_PAGE.CTA_SECTION.FORM_TITLE
            }
            formSubtitle={
              DISINFESTAZIONE_PAGE.CTA_SECTION.FORM_SUBTITLE
            }
            defaultService={
              DISINFESTAZIONE_PAGE.CTA_SECTION.DEFAULT_SERVICE
            }
            phoneNumber={CONTACT_INFO.MAIN_PHONE}
          />
        </div>
      </section>
    </div>
  );
}